import { useEffect, useState, useMemo } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import { useAppTranslation } from "locale/useAppTranslation";
import { freezerModelPageRouteName } from "../list/index.page";
import { parseFormattedNumber, yupNumTransform } from "_helper/formatters";
import {
  useFreezerModelServiceV1ListFreezerBrandsQuery,
  useFreezerModelServiceV1BatchCreateFreezerModelMutation,
  useFreezerModelServiceV1CreateFreezerBrandMutation,
} from "_services/modules/freezer_model";
import { toast } from "react-toastify";
import { errorHandler } from "_services/errorHandler";

interface FreezerModelItem {
  freezerBrandId: string;
  freezerModelName: string;
  fuelEfficiency: number;
  status: string;
}

export interface AddFreezerModelFormValues {
  freezerBrandId: string;
  freezerModelName: string;
  fuelEfficiency: string;
  status: string;
  items: FreezerModelItem[];
}

export const defaultFreezerBrandOptions = [
  { label: "Thermo King", value: "Thermo King" },
  { label: "Carrier", value: "Carrier" },
  { label: "Daikin", value: "Daikin" },
  { label: "Mitsubishi Heavy Industries", value: "Mitsubishi Heavy Industries" },
];

const useAddFreezerModelForm = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useAppTranslation("freezerModel");

  const schema = useMemo(() => yup.object({
    freezerBrandId: yup.string().required(),
    freezerModelName: yup.string().required(),
    fuelEfficiency: yup
      .number()
      .transform(yupNumTransform)
      .typeError(t("error-type-number"))
      .required()
      .min(0),
    status: yup.string().required(),
    items: yup.array().of(
      yup.object({
        freezerBrandId: yup.string().required(),
        freezerModelName: yup.string().required(),
        fuelEfficiency: yup.number().required().min(0),
        status: yup.string().required(),
      })
    ).required(),
  }).required(), [t]);

  const { data: brandsData } = useFreezerModelServiceV1ListFreezerBrandsQuery({});
  const [batchCreateFreezerModel] = useFreezerModelServiceV1BatchCreateFreezerModelMutation();
  const [createFreezerBrand] = useFreezerModelServiceV1CreateFreezerBrandMutation();

  const brandOptions = useMemo(() => {
    return brandsData?.freezer_brands?.map((brand) => ({
      label: brand.name ?? "",
      value: brand.id ?? "",
    })) || [];
  }, [brandsData]);

  const {
    control,
    register,
    trigger,
    getValues,
    setValue,
    setError,
    formState: { errors },
  } = useForm<AddFreezerModelFormValues>({
    mode: "onBlur",
    resolver: yupResolver(schema) as any,
    defaultValues: {
      freezerBrandId: "",
      freezerModelName: "",
      fuelEfficiency: "",
      status: "active",
      items: [],
    },
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "items",
  });

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-add"),
        hasBackButton: true,
      })
    );
  }, [dispatch, t]);

  const handleCreateBrand = async (term: string) => {
    try {
      const result = await createFreezerBrand({
        freezerModelV1CreateFreezerBrandReq: { name: term }
      }).unwrap();
      
      const newOption = { 
        label: term, 
        value: result.id ?? term 
      };
      return newOption;
    } catch (error) {
      console.error("Failed to create brand:", error);
      return { label: term, value: term };
    }
  };

  const handleAddToList = async () => {
    const fieldsToValidate: (keyof AddFreezerModelFormValues)[] = [
      "freezerBrandId",
      "freezerModelName",
      "fuelEfficiency",
      "status",
    ];

    const isValid = await trigger(fieldsToValidate);
    if (!isValid) return;

    const values = getValues();

    // Duplicate check: freezerModelName must be unique
    const isDuplicate = fields.some(
      (item, index) =>
        item.freezerModelName.toLowerCase() === values.freezerModelName.toLowerCase() &&
        index !== editingIndex
    );

    if (isDuplicate) {
      setError("freezerModelName", {
        type: "validate",
        message: t("error-duplicate-exists"),
      });
      return;
    }

    const newItem: FreezerModelItem = {
      freezerBrandId: values.freezerBrandId,
      freezerModelName: values.freezerModelName,
      fuelEfficiency: parseFormattedNumber(values.fuelEfficiency),
      status: values.status,
    };

    if (editingIndex !== null) {
      update(editingIndex, newItem);
      setEditingIndex(null);
    } else {
      append(newItem);
    }

    resetFormFields();
  };

  const handleEditItem = (index: number) => {
    const item = fields[index];
    setEditingIndex(index);
    setValue("freezerBrandId", item.freezerBrandId);
    setValue("freezerModelName", item.freezerModelName);
    setValue("fuelEfficiency", String(item.fuelEfficiency));
    setValue("status", item.status);
  };

  const handleDeleteItem = (index: number) => {
    remove(index);
    if (editingIndex === index) {
      setEditingIndex(null);
      resetFormFields();
    } else if (editingIndex !== null && index < editingIndex) {
      setEditingIndex(editingIndex - 1);
    }
  };

  const handleDiscardChanges = () => {
    setEditingIndex(null);
    resetFormFields();
  };

  const resetFormFields = () => {
    setValue("freezerBrandId", "");
    setValue("freezerModelName", "");
    setValue("fuelEfficiency", "");
    setValue("status", "active");
  };

  const handleSave = async () => {
    const payload = fields.map((f) => ({
      freezer_brand_id: f.freezerBrandId,
      freezer_model_name: f.freezerModelName,
      fuel_efficiency: f.fuelEfficiency,
      status: f.status,
    }));
    
    try {
      await batchCreateFreezerModel({
        freezerModelV1BatchCreateFreezerModelReq: { freezer_models: payload },
      }).unwrap();
      toast.success(t("toast-create-success"));
      setShowSaveModal(false);
      navigate(-1);
    } catch (error) {
      errorHandler(error);
      setShowSaveModal(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return {
    control,
    register,
    errors,
    fields,
    editingIndex,
    showSaveModal,
    setShowSaveModal,
    handleAddToList,
    handleEditItem,
    handleDeleteItem,
    handleDiscardChanges,
    handleSave,
    handleCancel,
    t,
    brandOptions,
    handleCreateBrand,
  };
};

export default useAddFreezerModelForm;
